import { useState, useEffect, useCallback } from 'react';

import { supabase } from '../lib/supabase';
import { dodajKwote, KwotyWgWalut } from '../lib/waluty';
import { useAuth } from '../providers/AuthProvider';

// ---------------------------------------------------------------------------
// Types (local — mirrors packages/types/marketplace)
// ---------------------------------------------------------------------------
export interface MarketplaceModule {
  id: string;
  name: string;
  description: string;
  long_description?: string;
  icon: string;
  version: string;
  category: string;
  tags: string[];
  status: string;
  is_public: boolean;
  author_type: 'internal' | 'partner';
  author_organization_id?: string;
  submission_status: string;
  deploy_url?: string;
  repository_url?: string;
  documentation_url?: string;
  screenshots: string[];
  featured: boolean;
  total_installs: number;
  avg_rating: number;
  review_count: number;
  price_free_limits: Record<string, number>;
  price_starter: number;
  price_professional: number;
  price_enterprise: number;
  price_perpetual: number;
  revenue_share_percent: number;
  support_email?: string;
  min_shell_version: string;
  changelog: { version: string; date: string; changes: string[] }[];
  created_at: string;
  updated_at: string;
}

export interface ModuleCategoryInfo {
  id: string;
  name: string;
  name_pl: string;
  description: string;
  icon: string;
  sort_order: number;
  /**
   * Zasieg polek Marketplace (migracja 487). Kategoria nie jest juz uniwersalna:
   * „Wagi i wazenie" nie opisuje zadnego modulu, a „Zasoby ludzkie" zadnej wagi.
   * Opcjonalne, bo wiersz sprzed migracji ich nie niesie — patrz `kategoriePolki`.
   */
  dla_oprogramowania?: boolean;
  dla_wtyczek?: boolean;
  dla_urzadzen?: boolean;
}

export interface ModuleReview {
  id: string;
  module_id: string;
  organization_id: string;
  reviewer_user_id: string;
  rating: number;
  title?: string;
  body?: string;
  is_verified_purchase: boolean;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Hook: useMarketplace — catalog browsing
// ---------------------------------------------------------------------------
export function useMarketplace() {
  const [modules, setModules] = useState<MarketplaceModule[]>([]);
  const [categories, setCategories] = useState<ModuleCategoryInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCatalog = useCallback(
    async (filters?: {
      category?: string;
      search?: string;
      featured?: boolean;
      sort?: 'popular' | 'newest' | 'rating' | 'price';
    }) => {
      try {
        setLoading(true);
        setError(null);

        let query = supabase
          .from('modules')
          .select('*')
          .eq('is_public', true)
          .eq('submission_status', 'published')
          .in('status', ['active', 'beta']);

        if (filters?.category && filters.category !== 'all') {
          query = query.eq('category', filters.category);
        }
        if (filters?.featured) {
          query = query.eq('featured', true);
        }
        if (filters?.search) {
          query = query.or(`name.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
        }

        switch (filters?.sort) {
          case 'popular':
            query = query.order('total_installs', { ascending: false });
            break;
          case 'newest':
            query = query.order('created_at', { ascending: false });
            break;
          case 'rating':
            query = query.order('avg_rating', { ascending: false });
            break;
          case 'price':
            query = query.order('price_starter', { ascending: true });
            break;
          default:
            query = query
              .order('featured', { ascending: false })
              .order('total_installs', { ascending: false });
        }

        const { data, error: fetchError } = await query;
        if (fetchError) throw fetchError;
        setModules(data || []);
      } catch (err) {
        console.error('[useMarketplace] Error:', err);
        setError('Nie udalo sie zaladowac katalogu modulow');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const fetchCategories = useCallback(async () => {
    const { data } = await supabase
      .from('module_categories')
      .select('*')
      .order('sort_order', { ascending: true });
    setCategories(data || []);
  }, []);

  useEffect(() => {
    fetchCatalog();
    fetchCategories();
  }, [fetchCatalog, fetchCategories]);

  return { modules, categories, loading, error, fetchCatalog, fetchCategories };
}

// ---------------------------------------------------------------------------
// Hook: useModuleReviews — ratings for a module
// ---------------------------------------------------------------------------
export function useModuleReviews(moduleId: string) {
  const [reviews, setReviews] = useState<ModuleReview[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('module_reviews')
      .select('*')
      .eq('module_id', moduleId)
      .eq('is_visible', true)
      .order('created_at', { ascending: false });
    setReviews(data || []);
    setLoading(false);
  }, [moduleId]);

  useEffect(() => {
    if (moduleId) fetchReviews();
  }, [moduleId, fetchReviews]);

  const submitReview = async (
    rating: number,
    title: string,
    body: string,
    organizationId: string,
    userId: string
  ) => {
    const { error } = await supabase.from('module_reviews').upsert(
      {
        module_id: moduleId,
        organization_id: organizationId,
        reviewer_user_id: userId,
        rating,
        title,
        body,
        is_verified_purchase: true,
      },
      { onConflict: 'module_id,organization_id' }
    );
    if (!error) fetchReviews();
    return { error };
  };

  return { reviews, loading, fetchReviews, submitReview };
}

// ---------------------------------------------------------------------------
// Hook: usePartnerModules — partner's own modules management
// ---------------------------------------------------------------------------
export interface PartnerModule extends MarketplaceModule {
  active_subscriptions?: number;
  total_revenue?: number;
  pending_payout?: number;
}

export function usePartnerModules() {
  const { currentOrganization } = useAuth();
  const [modules, setModules] = useState<PartnerModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMyModules = useCallback(async () => {
    if (!currentOrganization) return;
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('modules')
        .select('*')
        .eq('author_organization_id', currentOrganization.organization_id)
        .order('created_at', { ascending: false });
      if (fetchError) throw fetchError;
      setModules(data || []);
    } catch (err) {
      console.error('[usePartnerModules] Error:', err);
      setError('Nie udalo sie zaladowac modulow');
    } finally {
      setLoading(false);
    }
  }, [currentOrganization]);

  useEffect(() => {
    fetchMyModules();
  }, [fetchMyModules]);

  const createModule = async (moduleData: {
    id: string;
    name: string;
    description: string;
    category: string;
    icon: string;
    deploy_url: string;
    price_starter: number;
    price_professional: number;
    price_enterprise: number;
    price_perpetual: number;
    long_description?: string;
    tags?: string[];
    documentation_url?: string;
    repository_url?: string;
    support_email?: string;
    min_shell_version?: string;
  }) => {
    if (!currentOrganization) return { error: 'No organization' };

    const { error } = await supabase.from('modules').insert({
      id: moduleData.id,
      name: moduleData.name,
      description: moduleData.description,
      category: moduleData.category,
      icon: moduleData.icon,
      deploy_url: moduleData.deploy_url || null,
      price_starter: moduleData.price_starter,
      price_professional: moduleData.price_professional,
      price_enterprise: moduleData.price_enterprise,
      price_perpetual: moduleData.price_perpetual,
      long_description: moduleData.long_description || null,
      tags: moduleData.tags || [],
      documentation_url: moduleData.documentation_url || null,
      repository_url: moduleData.repository_url || null,
      support_email: moduleData.support_email || null,
      min_shell_version: moduleData.min_shell_version || '1.0.0',
      author_organization_id: currentOrganization.organization_id,
      author_type: 'partner',
      submission_status: 'draft',
      status: 'active',
      is_public: false,
    });
    if (!error) fetchMyModules();
    return { error };
  };

  const updateModule = async (moduleId: string, updates: Partial<MarketplaceModule>) => {
    const { error } = await supabase
      .from('modules')
      .update(updates)
      .eq('id', moduleId)
      .eq('author_organization_id', currentOrganization?.organization_id);
    if (!error) fetchMyModules();
    return { error };
  };

  const submitForReview = async (
    moduleId: string,
    deployUrl: string,
    description: string,
    screenshots: string[]
  ) => {
    // Create submission record
    const { error: subError } = await supabase.from('module_submissions').insert({
      module_id: moduleId,
      submitted_by: (await supabase.auth.getUser()).data.user?.id,
      version: '1.0.0',
      deploy_url: deployUrl,
      description,
      screenshots,
      status: 'pending',
    });
    if (subError) return { error: subError };

    // Update module status
    const { error: modError } = await supabase
      .from('modules')
      .update({ submission_status: 'submitted' })
      .eq('id', moduleId);
    if (!modError) fetchMyModules();
    return { error: modError };
  };

  return { modules, loading, error, fetchMyModules, createModule, updateModule, submitForReview };
}

// ---------------------------------------------------------------------------
// Hook: useModuleSubmissions — super admin review queue
// ---------------------------------------------------------------------------
export interface ModuleSubmission {
  id: string;
  module_id: string;
  submitted_by: string;
  version: string;
  deploy_url: string;
  repository_url?: string;
  documentation_url?: string;
  screenshots: string[];
  description?: string;
  changelog_entry?: string;
  status: string;
  assigned_reviewer?: string;
  submitted_at: string;
  reviewed_at?: string;
  review_notes?: string;
  security_scan_status: string;
  security_scan_report: Record<string, unknown>;
  performance_score?: number;
  accessibility_score?: number;
  compatibility_check: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export function useModuleSubmissions() {
  const [submissions, setSubmissions] = useState<(ModuleSubmission & { module_name?: string })[]>(
    []
  );
  const [loading, setLoading] = useState(true);

  const fetchSubmissions = useCallback(async (statusFilter?: string) => {
    setLoading(true);
    let query = supabase
      .from('module_submissions')
      .select('*, modules(name, author_organization_id, category, icon)')
      .order('submitted_at', { ascending: false });

    if (statusFilter && statusFilter !== 'all') {
      query = query.eq('status', statusFilter);
    }

    const { data } = await query;
    setSubmissions(data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const reviewSubmission = async (
    submissionId: string,
    decision: 'approved' | 'rejected' | 'changes_requested',
    notes: string,
    securityStatus: string,
    performanceScore: number,
    accessibilityScore: number
  ) => {
    const reviewer = (await supabase.auth.getUser()).data.user?.id;

    const { error: subError } = await supabase
      .from('module_submissions')
      .update({
        status: decision,
        assigned_reviewer: reviewer,
        reviewed_at: new Date().toISOString(),
        review_notes: notes,
        security_scan_status: securityStatus,
        performance_score: performanceScore,
        accessibility_score: accessibilityScore,
      })
      .eq('id', submissionId);

    if (subError) return { error: subError };

    // If approved, update the module status
    if (decision === 'approved') {
      const submission = submissions.find((s) => s.id === submissionId);
      if (submission) {
        await supabase
          .from('modules')
          .update({
            submission_status: 'approved',
            is_public: true,
          })
          .eq('id', submission.module_id);
      }
    } else if (decision === 'rejected') {
      const submission = submissions.find((s) => s.id === submissionId);
      if (submission) {
        await supabase
          .from('modules')
          .update({ submission_status: decision })
          .eq('id', submission.module_id);
      }
    } else if (decision === 'changes_requested') {
      const submission = submissions.find((s) => s.id === submissionId);
      if (submission) {
        await supabase
          .from('modules')
          .update({ submission_status: 'changes_requested' })
          .eq('id', submission.module_id);
      }
    }

    fetchSubmissions();
    return { error: null };
  };

  const publishModule = async (submissionId: string) => {
    const submission = submissions.find((s) => s.id === submissionId);
    if (!submission) return { error: 'Submission not found' };

    const { error } = await supabase
      .from('modules')
      .update({
        submission_status: 'published',
        is_public: true,
        deploy_url: submission.deploy_url,
      })
      .eq('id', submission.module_id);

    if (!error) fetchSubmissions();
    return { error };
  };

  return { submissions, loading, fetchSubmissions, reviewSubmission, publishModule };
}

// ---------------------------------------------------------------------------
// Hook: usePartnerRevenue — partner earnings dashboard
// ---------------------------------------------------------------------------
export interface RevenueEntry {
  id: string;
  module_id: string;
  gross_amount: number;
  platform_fee_amount: number;
  partner_amount: number;
  currency: string;
  payout_status: string;
  created_at: string;
  module_name?: string;
}

export function usePartnerRevenue() {
  const { currentOrganization } = useAuth();
  const [revenue, setRevenue] = useState<RevenueEntry[]>([]);
  const [stats, setStats] = useState<{
    totalEarnings: KwotyWgWalut;
    pendingPayout: KwotyWgWalut;
    paidOut: KwotyWgWalut;
    thisMonth: KwotyWgWalut;
  }>({
    totalEarnings: {},
    pendingPayout: {},
    paidOut: {},
    thisMonth: {},
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRevenue = useCallback(async () => {
    if (!currentOrganization) return;
    setLoading(true);
    setError(null);

    const { data, error: revError } = await supabase
      .from('module_revenue')
      .select('*, modules(name)')
      .eq('author_organization_id', currentOrganization.organization_id)
      .order('created_at', { ascending: false });

    if (revError) {
      // Bez tego odrzucone zapytanie (RLS/awaria) renderowało „Brak transakcji"
      // i zera w kaflach — nieodróżnialne od realnie pustych przychodów.
      console.error('Nie udało się pobrać przychodów modułów:', revError);
      setError(revError.message);
      setRevenue([]);
      setLoading(false);
      return;
    }

    // Nazwa modułu wraca zagnieżdżona (`modules.name`); UI czyta płaskie
    // `module_name`, więc bez tego mapowania kolumna „Moduł" pokazywała surowy id.
    const entries: RevenueEntry[] = (data || []).map((row) => {
      const { modules, ...reszta } = row as RevenueEntry & { modules?: { name?: string } | null };
      return { ...reszta, module_name: modules?.name };
    });
    setRevenue(entries);

    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

    // Sumy per waluta — wiersze module_revenue niosą currency (EUR i PLN),
    // a suma mieszanych walut w jednej liczbie kłamie niezależnie od etykiety.
    const next = {
      totalEarnings: {} as KwotyWgWalut,
      pendingPayout: {} as KwotyWgWalut,
      paidOut: {} as KwotyWgWalut,
      thisMonth: {} as KwotyWgWalut,
    };
    entries.forEach((e) => {
      const kwota = Number(e.partner_amount);
      dodajKwote(next.totalEarnings, e.currency, kwota);
      if (e.payout_status === 'pending') dodajKwote(next.pendingPayout, e.currency, kwota);
      if (e.payout_status === 'paid') dodajKwote(next.paidOut, e.currency, kwota);
      if (e.created_at >= monthStart) dodajKwote(next.thisMonth, e.currency, kwota);
    });
    setStats(next);

    setLoading(false);
  }, [currentOrganization]);

  useEffect(() => {
    fetchRevenue();
  }, [fetchRevenue]);

  return { revenue, stats, loading, error, fetchRevenue };
}
